import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { SXModalBase } from "./SXModal";
import { getClientId } from "../../App";

type CallPath = "gateway" | "direct";

type CellResult =
  | { ok: true; durationMs: number; bytesReceived: number }
  | { ok: false; error: string; httpStatus?: number };

type RowResult = {
  fileSizeMb: number;
  gateway?: CellResult;
  direct?: CellResult;
};

type ValidRow = {
  fileSizeMb: number;
  gateway: Extract<CellResult, { ok: true }>;
  direct: Extract<CellResult, { ok: true }>;
};

type Props = {
  show: boolean;
  onHide: () => void;
};

const SIZES = [2, 5, 10, 20, 50] as const;
const PATHS: CallPath[] = ["gateway", "direct"];
const RUNS = 3; // timed runs per cell — results are averaged

async function callBenchmark(fileSizeMb: number, path: CallPath, headers: Record<string, string>): Promise<CellResult> {
  let resp: Response;
  try {
    resp = await fetch("/api/benchmark/payload", {
      method: "POST",
      headers,
      body: JSON.stringify({ fileSizeMb, path }),
    });
  } catch (err: any) {
    return { ok: false, error: `Network error: ${err?.message ?? "unknown"}` };
  }

  let body: any;
  try {
    body = await resp.json();
  } catch {
    return { ok: false, error: `HTTP ${resp.status} — could not parse response`, httpStatus: resp.status };
  }

  if (!resp.ok) {
    const detail = body?.error ?? `HTTP ${resp.status}`;
    return { ok: false, error: detail, httpStatus: resp.status };
  }

  return {
    ok: true,
    durationMs: body.durationMs,
    bytesReceived: body.bytesReceived,
  };
}

// ── Chart helpers ──────────────────────────────────────────────────────────

function linReg(pairs: Array<[number, number]>): { slope: number; intercept: number } | null {
  const n = pairs.length;
  if (n < 2) return null;
  const sx = pairs.reduce((a, [x]) => a + x, 0);
  const sy = pairs.reduce((a, [, y]) => a + y, 0);
  const sxy = pairs.reduce((a, [x, y]) => a + x * y, 0);
  const sxx = pairs.reduce((a, [x]) => a + x * x, 0);
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-10) return null;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

const SVG_W = 480;
const SVG_PAD = { top: 28, right: 16, bottom: 40, left: 62 };
const SVG_CW = SVG_W - SVG_PAD.left - SVG_PAD.right;

function makeLogXScale(rows: ValidRow[]) {
  const logMin = Math.log10(rows[0].fileSizeMb);
  const logMax = Math.log10(rows[rows.length - 1].fileSizeMb);
  const xOf = (mb: number) => SVG_PAD.left + ((Math.log10(mb) - logMin) / (logMax - logMin)) * SVG_CW;
  return { xOf, logMin, logMax };
}

function fmtMs(v: number) {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(Math.abs(v) < 10000 ? 1 : 0)}s` : `${Math.round(v)}`;
}

// Chart 1: gateway + direct duration vs file size (log X). Shows whichever paths have data.
function ChartDuration({ results }: { results: RowResult[] }) {
  const gwRows = results.filter((r) => r.gateway?.ok) as Array<
    RowResult & { gateway: Extract<CellResult, { ok: true }> }
  >;
  const dirRows = results.filter((r) => r.direct?.ok) as Array<
    RowResult & { direct: Extract<CellResult, { ok: true }> }
  >;

  if (gwRows.length < 2 && dirRows.length < 2) return null;

  const H = 175;
  const cH = H - SVG_PAD.top - SVG_PAD.bottom;

  // X scale spans all tested sizes for consistent positioning
  const allSizes = results.map((r) => r.fileSizeMb);
  const logMin = Math.log10(Math.min(...allSizes));
  const logMax = Math.log10(Math.max(...allSizes));
  const xOf = (mb: number) => SVG_PAD.left + ((Math.log10(mb) - logMin) / (logMax - logMin)) * SVG_CW;

  const allMs = [...gwRows.map((r) => r.gateway.durationMs), ...dirRows.map((r) => r.direct.durationMs)];
  const yMax = Math.max(...allMs) * 1.15;
  const yOf = (ms: number) => SVG_PAD.top + cH * (1 - ms / yMax);
  const yTicks = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  const gwPts = gwRows.map((r) => `${xOf(r.fileSizeMb).toFixed(1)},${yOf(r.gateway.durationMs).toFixed(1)}`).join(" ");
  const dirPts = dirRows.map((r) => `${xOf(r.fileSizeMb).toFixed(1)},${yOf(r.direct.durationMs).toFixed(1)}`).join(" ");

  let legendX = SVG_PAD.left + 8;
  const legendItem = (x: number, color: string, label: string) => (
    <g key={label}>
      <line x1={x} y1={SVG_PAD.top + 14} x2={x + 18} y2={SVG_PAD.top + 14} stroke={color} strokeWidth={2} />
      <circle cx={x + 9} cy={SVG_PAD.top + 14} r={3.5} fill={color} />
      <text x={x + 22} y={SVG_PAD.top + 18} fill="#374151" fontSize={11}>
        {label}
      </text>
    </g>
  );
  const legendItems: React.ReactNode[] = [];
  if (gwRows.length >= 1) {
    legendItems.push(legendItem(legendX, "#ef4444", "Via gateway"));
    legendX += 112;
  }
  if (dirRows.length >= 1) {
    legendItems.push(legendItem(legendX, "#3b82f6", "Direct to API"));
  }

  return (
    <svg width="100%" viewBox={`0 0 ${SVG_W} ${H}`} style={{ display: "block", fontFamily: "inherit" }}>
      <text x={SVG_W / 2} y={14} textAnchor="middle" fill="#374151" fontSize={12} fontWeight={600}>
        Duration vs File Size
      </text>

      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={SVG_PAD.left} y1={yOf(v)} x2={SVG_PAD.left + SVG_CW} y2={yOf(v)} stroke="#e5e7eb" strokeWidth={1} />
          <text x={SVG_PAD.left - 5} y={yOf(v) + 4} textAnchor="end" fill="#9ca3af" fontSize={10}>
            {fmtMs(v)}
          </text>
        </g>
      ))}

      <line
        x1={SVG_PAD.left}
        y1={SVG_PAD.top}
        x2={SVG_PAD.left}
        y2={SVG_PAD.top + cH}
        stroke="#d1d5db"
        strokeWidth={1}
      />
      <line
        x1={SVG_PAD.left}
        y1={SVG_PAD.top + cH}
        x2={SVG_PAD.left + SVG_CW}
        y2={SVG_PAD.top + cH}
        stroke="#d1d5db"
        strokeWidth={1}
      />

      {results.map((r) => (
        <g key={r.fileSizeMb}>
          <line
            x1={xOf(r.fileSizeMb)}
            y1={SVG_PAD.top + cH}
            x2={xOf(r.fileSizeMb)}
            y2={SVG_PAD.top + cH + 4}
            stroke="#d1d5db"
          />
          <text x={xOf(r.fileSizeMb)} y={SVG_PAD.top + cH + 15} textAnchor="middle" fill="#9ca3af" fontSize={9}>
            {r.fileSizeMb}
          </text>
        </g>
      ))}
      <text x={SVG_PAD.left + SVG_CW / 2} y={H - 2} textAnchor="middle" fill="#9ca3af" fontSize={10}>
        Payload (MB, log scale)
      </text>
      <text
        transform={`rotate(-90, 14, ${SVG_PAD.top + cH / 2})`}
        x={14}
        y={SVG_PAD.top + cH / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#9ca3af"
        fontSize={10}
      >
        ms
      </text>

      {gwRows.length >= 2 && (
        <polyline points={gwPts} fill="none" stroke="#ef4444" strokeWidth={2} strokeLinejoin="round" />
      )}
      {dirRows.length >= 2 && (
        <polyline points={dirPts} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" />
      )}

      {gwRows.map((r) => (
        <circle key={r.fileSizeMb} cx={xOf(r.fileSizeMb)} cy={yOf(r.gateway.durationMs)} r={3.5} fill="#ef4444">
          <title>{`${r.fileSizeMb} MB via gateway: ${r.gateway.durationMs.toLocaleString()} ms`}</title>
        </circle>
      ))}
      {dirRows.map((r) => (
        <circle key={r.fileSizeMb} cx={xOf(r.fileSizeMb)} cy={yOf(r.direct.durationMs)} r={3.5} fill="#3b82f6">
          <title>{`${r.fileSizeMb} MB direct: ${r.direct.durationMs.toLocaleString()} ms`}</title>
        </circle>
      ))}

      {legendItems}
    </svg>
  );
}

// Chart 2: overhead (gateway − direct) vs file size (log X) + linear trend
function ChartOverhead({ rows, allResults }: { rows: ValidRow[]; allResults: RowResult[] }) {
  if (rows.length < 2) return null;

  const H = 160;
  const cH = H - SVG_PAD.top - SVG_PAD.bottom;

  // X scale spans all tested sizes for consistency with ChartDuration
  const allSizes = allResults.map((r) => r.fileSizeMb);
  const logMin = Math.log10(Math.min(...allSizes));
  const logMax = Math.log10(Math.max(...allSizes));
  const xOf = (mb: number) => SVG_PAD.left + ((Math.log10(mb) - logMin) / (logMax - logMin)) * SVG_CW;

  const overheads = rows.map((r) => r.gateway.durationMs - r.direct.durationMs);
  const ovMin = Math.min(0, ...overheads);
  const ovMax = Math.max(0, ...overheads);
  const yPad = Math.max(30, (ovMax - ovMin) * 0.15);
  const yLo = ovMin - yPad;
  const yHi = ovMax + yPad;
  const yRange = yHi - yLo;
  const yOf = (v: number) => SVG_PAD.top + cH * (1 - (v - yLo) / yRange);
  const zeroY = yOf(0);

  // Fit regression on (log10(mb), overhead) — slope is ms per order-of-magnitude (decade)
  const logPairs: Array<[number, number]> = rows.map((r, i) => [Math.log10(r.fileSizeMb), overheads[i]]);
  const reg = linReg(logPairs);

  const pts = rows.map((r, i) => `${xOf(r.fileSizeMb).toFixed(1)},${yOf(overheads[i]).toFixed(1)}`).join(" ");

  const trendY0 = reg ? Math.max(SVG_PAD.top, Math.min(SVG_PAD.top + cH, yOf(reg.slope * logMin + reg.intercept))) : 0;
  const trendY1 = reg ? Math.max(SVG_PAD.top, Math.min(SVG_PAD.top + cH, yOf(reg.slope * logMax + reg.intercept))) : 0;

  const yTicks = Array.from({ length: 5 }, (_, i) => yLo + (yRange / 4) * i);

  return (
    <svg width="100%" viewBox={`0 0 ${SVG_W} ${H}`} style={{ display: "block", fontFamily: "inherit" }}>
      <text x={SVG_W / 2} y={14} textAnchor="middle" fill="#374151" fontSize={12} fontWeight={600}>
        Gateway Overhead
      </text>

      {/* Y grid + labels */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={SVG_PAD.left} y1={yOf(v)} x2={SVG_PAD.left + SVG_CW} y2={yOf(v)} stroke="#e5e7eb" strokeWidth={1} />
          <text x={SVG_PAD.left - 5} y={yOf(v) + 4} textAnchor="end" fill="#9ca3af" fontSize={10}>
            {fmtMs(v)}
          </text>
        </g>
      ))}

      {/* Zero baseline */}
      <line
        x1={SVG_PAD.left}
        y1={zeroY}
        x2={SVG_PAD.left + SVG_CW}
        y2={zeroY}
        stroke="#9ca3af"
        strokeWidth={1}
        strokeDasharray="3,3"
      />

      {/* Axes */}
      <line
        x1={SVG_PAD.left}
        y1={SVG_PAD.top}
        x2={SVG_PAD.left}
        y2={SVG_PAD.top + cH}
        stroke="#d1d5db"
        strokeWidth={1}
      />
      <line
        x1={SVG_PAD.left}
        y1={SVG_PAD.top + cH}
        x2={SVG_PAD.left + SVG_CW}
        y2={SVG_PAD.top + cH}
        stroke="#d1d5db"
        strokeWidth={1}
      />

      {/* X ticks — all tested sizes */}
      {allResults.map((r) => (
        <g key={r.fileSizeMb}>
          <line
            x1={xOf(r.fileSizeMb)}
            y1={SVG_PAD.top + cH}
            x2={xOf(r.fileSizeMb)}
            y2={SVG_PAD.top + cH + 4}
            stroke="#d1d5db"
          />
          <text x={xOf(r.fileSizeMb)} y={SVG_PAD.top + cH + 15} textAnchor="middle" fill="#9ca3af" fontSize={9}>
            {r.fileSizeMb}
          </text>
        </g>
      ))}
      <text x={SVG_PAD.left + SVG_CW / 2} y={H - 2} textAnchor="middle" fill="#9ca3af" fontSize={10}>
        Payload (MB, log scale)
      </text>

      {/* Y axis label */}
      <text
        transform={`rotate(-90, 14, ${SVG_PAD.top + cH / 2})`}
        x={14}
        y={SVG_PAD.top + cH / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#9ca3af"
        fontSize={10}
      >
        ms
      </text>

      {/* Trend line */}
      {reg && (
        <line
          x1={xOf(rows[0].fileSizeMb)}
          y1={trendY0}
          x2={xOf(rows[rows.length - 1].fileSizeMb)}
          y2={trendY1}
          stroke="#f59e0b"
          strokeWidth={1.5}
          strokeDasharray="5,3"
        />
      )}

      {/* Overhead line */}
      <polyline points={pts} fill="none" stroke="#8b5cf6" strokeWidth={2} strokeLinejoin="round" />

      {/* Dots */}
      {rows.map((r, i) => (
        <circle key={r.fileSizeMb} cx={xOf(r.fileSizeMb)} cy={yOf(overheads[i])} r={3.5} fill="#8b5cf6">
          <title>{`${r.fileSizeMb} MB overhead: ${overheads[i] >= 0 ? "+" : ""}${overheads[i].toLocaleString()} ms`}</title>
        </circle>
      ))}

      {/* Legend */}
      <line
        x1={SVG_PAD.left + 8}
        y1={SVG_PAD.top + 14}
        x2={SVG_PAD.left + 26}
        y2={SVG_PAD.top + 14}
        stroke="#8b5cf6"
        strokeWidth={2}
      />
      <circle cx={SVG_PAD.left + 17} cy={SVG_PAD.top + 14} r={3.5} fill="#8b5cf6" />
      <text x={SVG_PAD.left + 30} y={SVG_PAD.top + 18} fill="#374151" fontSize={11}>
        Overhead (gateway − direct)
      </text>
      {reg && (
        <>
          <line
            x1={SVG_PAD.left + 218}
            y1={SVG_PAD.top + 14}
            x2={SVG_PAD.left + 236}
            y2={SVG_PAD.top + 14}
            stroke="#f59e0b"
            strokeWidth={1.5}
            strokeDasharray="5,3"
          />
          <text x={SVG_PAD.left + 240} y={SVG_PAD.top + 18} fill="#374151" fontSize={11}>
            Trend ({reg.slope >= 0 ? "+" : ""}
            {Math.round(reg.slope)} ms/decade)
          </text>
        </>
      )}
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function BenchmarkModal({ show, onHide }: Props) {
  const { getAccessTokenSilently } = useAuth0();
  const [running, setRunning] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [results, setResults] = useState<RowResult[]>([]);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const activeRunIdRef = useRef(0);

  const runBenchmark = useCallback(async () => {
    const runId = ++activeRunIdRef.current;
    const isCurrentRun = () => activeRunIdRef.current === runId;

    setRunning(true);
    setResults([]);
    setTokenError(null);
    setProgressLabel("");

    let token = "";
    try {
      token = await getAccessTokenSilently();
    } catch (err: any) {
      if (!isCurrentRun()) return;
      setTokenError(`Could not obtain auth token: ${err?.message ?? "unknown error"}`);
      setRunning(false);
      return;
    }

    if (!isCurrentRun()) return;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-Client-ID": getClientId(),
    };

    const acc: Record<number, RowResult> = {};
    for (const s of SIZES) acc[s] = { fileSizeMb: s };

    // ── Warm-up ─────────────────────────────────────────────────────────────
    // Fire one silent 2 MB call on each path before measuring. This triggers
    // .NET JIT compilation and TCP connection setup so they don't skew run 1.
    setProgressLabel("Warming up — initialising server connections… (1/2)");
    await callBenchmark(2, "gateway", headers);
    if (!isCurrentRun()) return;
    setProgressLabel("Warming up — initialising server connections… (2/2)");
    await callBenchmark(2, "direct", headers);
    if (!isCurrentRun()) return;

    // ── Timed runs ──────────────────────────────────────────────────────────
    // All gateway sizes first, then all direct sizes — equal warm-up for both.
    // Each cell is measured RUNS times and the durations are averaged.
    const totalMeasured = SIZES.length * PATHS.length * RUNS;
    let measuredCount = 0;

    for (const path of PATHS) {
      for (const fileSizeMb of SIZES) {
        const durations: number[] = [];
        let lastOk: Extract<CellResult, { ok: true }> | null = null;
        let lastErr: Extract<CellResult, { ok: false }> | null = null;

        for (let run = 1; run <= RUNS; run++) {
          measuredCount++;
          setProgressLabel(
            `${path === "gateway" ? "Gateway" : "Direct"} ${fileSizeMb} MB — run ${run}/${RUNS}  (${measuredCount}/${totalMeasured})`,
          );
          const result = await callBenchmark(fileSizeMb, path, headers);
          if (!isCurrentRun()) return;
          if (result.ok) {
            durations.push(result.durationMs);
            lastOk = result;
          } else lastErr = result;
        }

        acc[fileSizeMb][path] =
          durations.length > 0 && lastOk
            ? {
                ok: true,
                durationMs: Math.round(durations.reduce((a, b) => a + b) / durations.length),
                bytesReceived: lastOk.bytesReceived,
              }
            : (lastErr ?? { ok: false, error: "All runs failed" });

        setResults(SIZES.map((s) => acc[s]));
      }
    }

    if (!isCurrentRun()) return;
    setRunning(false);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!show) {
      activeRunIdRef.current++;
      setRunning(false);
      setProgressLabel("");
      return;
    }

    void runBenchmark();

    return () => {
      activeRunIdRef.current++;
    };
  }, [show, runBenchmark]);

  // ── Formatting helpers ─────────────────────────────────────────────────
  const fmtDuration = (ms: number) => `${ms.toLocaleString()} ms`;
  const fmtMB = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

  const overheadCell = (gw?: CellResult, direct?: CellResult) => {
    if (!gw || !direct || !gw.ok || !direct.ok) return { text: "—", color: undefined };
    const diff = gw.durationMs - direct.durationMs;
    const sign = diff >= 0 ? "+" : "";
    const pct = direct.durationMs > 0 ? `${sign}${((diff / direct.durationMs) * 100).toFixed(0)}%` : "—";
    // Negative overhead (gateway appears faster) is a measurement artefact from
    // Node.js streaming efficiency vs .NET CopyToAsync at large payload sizes —
    // not a real gateway benefit. Show it as neutral grey, not celebratory green.
    return {
      text: `${sign}${diff.toLocaleString()} ms (${pct})`,
      color: diff > 0 ? "#b91c1c" : diff < -10 ? "#6b7280" : "#374151",
    };
  };

  const cellContent = (result?: CellResult) => {
    if (!result) return <span style={{ color: "#6b7280" }}>—</span>;
    if (!result.ok) {
      const statusHint = result.httpStatus ? ` [HTTP ${result.httpStatus}]` : "";
      return (
        <span style={{ color: "#92400e", fontSize: "0.82em" }} title={result.error}>
          ⚠ {result.error}
          {statusHint}
        </span>
      );
    }
    return (
      <>
        {fmtDuration(result.durationMs)}
        <br />
        <small style={{ color: "#6b7280" }}>{fmtMB(result.bytesReceived)} received</small>
      </>
    );
  };

  // ── Modal body ─────────────────────────────────────────────────────────
  const validRows = results.filter((r): r is ValidRow => !!(r.gateway?.ok && r.direct?.ok));

  // Plain-language conclusion: interpret direction of scaling, not just magnitude
  const conclusion = (() => {
    if (validRows.length < 2) return null;
    const overheads = validRows.map((r) => r.gateway.durationMs - r.direct.durationMs);
    const logPairs: Array<[number, number]> = validRows.map((r, i) => [Math.log10(r.fileSizeMb), overheads[i]]);
    const reg = linReg(logPairs);
    if (!reg) return null;

    // Use the smallest-size overhead as the best proxy for fixed per-request cost
    // (auth/routing), since it is least affected by the streaming-client difference.
    const fixedOverhead = overheads[0];
    const fixedSign = fixedOverhead >= 0 ? "+" : "";

    if (reg.slope > 50) {
      // Overhead is genuinely growing with payload size — concerning
      return {
        text: `Per-request overhead: ${fixedSign}${fixedOverhead} ms. WARNING: overhead grows ~${Math.round(reg.slope)} ms per 10× increase in payload size.`,
        ok: false,
      };
    }
    if (reg.slope < -50) {
      // Overhead decreases as size grows — gateway adds a fixed cost, not a per-byte cost
      return {
        text: `Per-request overhead: ${fixedSign}${fixedOverhead} ms (auth/routing). Transfer overhead: negligible — does not scale with payload size.`,
        ok: true,
      };
    }
    // Flat — overhead is constant across sizes
    const avg = Math.round(overheads.reduce((a, b) => a + b) / overheads.length);
    const avgSign = avg >= 0 ? "+" : "";
    return {
      text: `Average gateway overhead: ${avgSign}${avg} ms — consistent across all payload sizes.`,
      ok: true,
    };
  })();

  const body = (() => {
    if (tokenError) {
      return (
        <div style={{ color: "#b91c1c", padding: "0.5rem 0" }}>
          <strong>Auth error:</strong> {tokenError}
        </div>
      );
    }
    if (running) {
      return (
        <div className="d-flex align-items-center gap-3 py-2">
          <Spinner animation="border" size="sm" />
          <span>{progressLabel}</span>
        </div>
      );
    }
    if (results.length === 0) return null;

    const allDirectFailed = results.length > 0 && results.every((r) => !r.direct?.ok);
    const allGatewayFailed = results.length > 0 && results.every((r) => !r.gateway?.ok);

    return (
      <>
        {(validRows.length >= 2 || (!allGatewayFailed && results.some((r) => r.gateway?.ok))) && (
          <div style={{ marginBottom: "1rem" }}>
            {allDirectFailed && (
              <p
                style={{
                  fontSize: "0.8em",
                  color: "#92400e",
                  background: "#fef3c7",
                  borderRadius: 4,
                  padding: "0.3rem 0.6rem",
                  marginBottom: "0.5rem",
                }}
              >
                Direct path unreachable — showing gateway only. Set <code>APP_FEM_DIRECT_URL</code> to override the
                Docker hostname for local dev.
              </p>
            )}
            <ChartDuration results={results} />
            {validRows.length >= 2 && (
              <div style={{ marginTop: "0.5rem" }}>
                <ChartOverhead rows={validRows} allResults={results} />
              </div>
            )}
            <p style={{ fontSize: "0.78em", color: "#9ca3af", marginTop: "0.25rem", marginBottom: "0.5rem" }}>
              X-axis is logarithmic so all sizes are evenly spaced. The overhead trend slope shows ms added per 10×
              increase in file size.
            </p>
          </div>
        )}
        {conclusion && (
          <div
            style={{
              padding: "0.5rem 0.75rem",
              marginBottom: "0.75rem",
              borderRadius: 4,
              background: conclusion.ok ? "#f0fdf4" : "#fef9c3",
              border: `1px solid ${conclusion.ok ? "#86efac" : "#fde047"}`,
              fontWeight: 600,
              fontSize: "0.9em",
              color: conclusion.ok ? "#166534" : "#854d0e",
            }}
          >
            {conclusion.text}
          </div>
        )}
        <Table bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Payload</th>
              <th>
                Via gateway <small style={{ fontWeight: 400, color: "#6b7280" }}>avg. {RUNS} runs</small>
              </th>
              <th>
                Direct to API <small style={{ fontWeight: 400, color: "#6b7280" }}>avg. {RUNS} runs</small>
              </th>
              <th>Gateway overhead</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const { text, color } = overheadCell(r.gateway, r.direct);
              return (
                <tr key={r.fileSizeMb}>
                  <td>{r.fileSizeMb} MB</td>
                  <td>{cellContent(r.gateway)}</td>
                  <td>{cellContent(r.direct)}</td>
                  <td style={{ color }}>{text}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </>
    );
  })();

  const footer = (
    <>
      {!running && (results.length > 0 || tokenError) && (
        <Button variant="outline-secondary" size="sm" onClick={runBenchmark}>
          Run again
        </Button>
      )}
      <Button variant="secondary" size="sm" onClick={onHide}>
        Close
      </Button>
    </>
  );

  return (
    <SXModalBase
      header="Payload Benchmark — Gateway vs. Direct"
      show={show}
      close={onHide}
      size="xl"
      body={body}
      footer={footer}
    />
  );
}
